import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
    Spotting,
    PaginatedResponse,
    SightingCategory
} from '../types/api'

// Query keys
export const spottingKeys = {
    all: ['spottings'] as const,
    lists: () => [...spottingKeys.all, 'list'] as const,
    list: (params?: Record<string, unknown>) => [...spottingKeys.lists(), params] as const,
    details: () => [...spottingKeys.all, 'detail'] as const,
    detail: (id: string) => [...spottingKeys.details(), id] as const,
    nearby: (lat: number, lng: number, radius?: number) =>
        [...spottingKeys.all, 'nearby', { lat, lng, radius }] as const,
    bounds: (bounds: Record<string, unknown> | null, options?: Record<string, unknown>) =>
        [...spottingKeys.all, 'bounds', bounds, options] as const,
}

// Sighting Categories Query Keys
export const sightingCategoryKeys = {
    all: ['sighting-categories'] as const,
    lists: () => [...sightingCategoryKeys.all, 'list'] as const,
}

// Hook to fetch sighting categories
export function useSightingCategories() {
    return useQuery({
        queryKey: sightingCategoryKeys.lists(),
        queryFn: async () => {
            const response = await api.sightingCategories.getAll()
            return response.data as SightingCategory[]
        },
        staleTime: 1000 * 60 * 10, // Categories don't change often, cache for 10 minutes
        gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    })
}

// Hook to fetch all sightings
export function useSpottings(params?: {
    page?: number
    limit?: number
    type?: string
}) {
    return useQuery({
        queryKey: spottingKeys.list(params),
        queryFn: async () => {
            const response = await api.sightings.getAll(params)
            return response.data as PaginatedResponse<Spotting>
        },
    })
}

// Hook to fetch sightings within map bounds
export function useSpottingsInBounds(
    bounds: {
        north: number
        south: number
        east: number
        west: number
    } | null,
    options?: {
        page?: number
        limit?: number
        enabled?: boolean
        categoryIds?: string[]
    }
) {
    // Round bounds to 4 decimal places to reduce query key variations
    // This prevents constant refetching while maintaining reasonable precision
    const roundedBounds = bounds ? {
        north: Math.round(bounds.north * 10000) / 10000,
        south: Math.round(bounds.south * 10000) / 10000,
        east: Math.round(bounds.east * 10000) / 10000,
        west: Math.round(bounds.west * 10000) / 10000,
    } : null

    return useQuery({
        queryKey: spottingKeys.bounds(roundedBounds, {
            page: options?.page || 1,
            limit: options?.limit || 100,
            categoryIds: options?.categoryIds || []
        }),
        queryFn: async () => {
            if (!bounds) return null

            const baseParams = {
                latMin: bounds.south,  // minimum latitude
                latMax: bounds.north,  // maximum latitude  
                lonMin: bounds.west,   // minimum longitude
                lonMax: bounds.east,   // maximum longitude
                page: options?.page || 1,
                limit: options?.limit || 100, // Use max limit of 100
            }

            // Build params object with proper typing
            const params: typeof baseParams & { categoryIds?: string[] } = { ...baseParams }

            // Add categoryIds based on selection
            if (options?.categoryIds && options.categoryIds.length > 0) {
                params.categoryIds = options.categoryIds
                console.log('🔍 Fetching sightings with category filter:', options.categoryIds)
            } else {
                // When no categories are selected, pass 'none' as the categoryId
                params.categoryIds = ['none']
                console.log('🔍 Fetching sightings with no categories selected (categoryIds: none)')
            }

            console.log('🌐 API call params:', params)
            const response = await api.sightings.getAll(params)
            console.log('📊 API response:', response.data)
            return response.data as PaginatedResponse<Spotting>
        },
        enabled: !!bounds && (options?.enabled !== false),
        staleTime: 1000 * 60 * 5, // Reduce stale time to 5 minutes for more responsive updates
        gcTime: 1000 * 60 * 15, // Reduce cache time to 15 minutes
        placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
    })
}

// Hook to fetch single sighting
export function useSpotting(id: string) {
    return useQuery({
        queryKey: spottingKeys.detail(id),
        queryFn: async () => {
            const response = await api.sightings.getById(id)
            return response.data as Spotting
        },
        enabled: !!id,
    })
}

// Hook to fetch nearby sightings
export function useNearbySpottings(lat: number, lng: number, radius?: number) {
    return useQuery({
        queryKey: spottingKeys.nearby(lat, lng, radius),
        queryFn: async () => {
            const response = await api.sightings.getNearby({ lat, lng, radius })
            return response.data as Spotting[]
        },
        enabled: !!(lat && lng),
    })
}

// Hook to create a new sighting
export function useCreateSpotting() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (formData: FormData) => {
            // print form data entries
            for (const [key, value] of formData.entries()) {
                console.log(`${key}: ${value}`);
            }
            const response = await api.sightings.create(formData);
            return response.data as Spotting;
        },
        onSuccess: () => {
            // Invalidate and refetch sightings queries
            queryClient.invalidateQueries({ queryKey: spottingKeys.all });
        },
    });
}

// Hook to update a sighting
export function useUpdateSpotting() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ id, data }: {
            id: string
            data: {
                name?: string
                description?: string
                categoryId?: string
                imageUrl?: string
                latitude?: number
                longitude?: number
            }
        }) => {
            const response = await api.sightings.update(id, data)
            return response.data as Spotting
        },
        onSuccess: (data) => {
            // Invalidate and refetch sightings queries
            queryClient.invalidateQueries({ queryKey: spottingKeys.all })
            // Update the specific sighting in cache
            queryClient.setQueryData(spottingKeys.detail(data.id), data)
        },
    })
}

// Hook to delete a sighting
export function useDeleteSpotting() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (id: string) => {
            await api.sightings.delete(id)
            return id
        },
        onSuccess: (deletedId) => {
            // Invalidate and refetch sightings queries
            queryClient.invalidateQueries({ queryKey: spottingKeys.all })
            // Remove the specific sighting from cache
            queryClient.removeQueries({ queryKey: spottingKeys.detail(deletedId) })
        },
    })
} 